import { useEffect, useState } from "react";

/**
 * Symmetrical pentagon hub: center goal, five peripheral gaps, animated edges.
 * Layout (counter-clockwise from bottom vertex): five gap labels around hub.
 */
const DESKTOP_HUB_SCALE = 0.88;
const MOBILE_HUB_SCALE = 1.2;

const CX = 400;
const CY = 320;
const NODES = [
  { key: "reminders", lines: ["Zero", "Reminders"] },
  { key: "coach", lines: ["No", "Coach"] },
  { key: "tracking", lines: ["No", "Tracking"] },
  { key: "workouts", lines: ["Missed", "Workouts"] },
  { key: "structure", lines: ["No", "Structure"] },
];

function vertexXY(index, radiusOuter) {
  const angle = Math.PI / 2 + (2 * Math.PI * index) / 5;
  return {
    x: CX + radiusOuter * Math.cos(angle),
    y: CY + radiusOuter * Math.sin(angle),
  };
}

export default function ProblemHubDiagram() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = (event) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const hubScale = isMobile ? MOBILE_HUB_SCALE : DESKTOP_HUB_SCALE;
  const radiusOuter = 200 * hubScale;
  const radiusCenter = 56 * hubScale;
  const radiusNode = 56 * hubScale;
  const verts = NODES.map((_, i) => vertexXY(i, radiusOuter));

  const radials = verts.map((v, i) => (
    <line
      key={`rad-${NODES[i].key}`}
      x1={CX}
      y1={CY}
      x2={v.x}
      y2={v.y}
      className="problem-hub-edge"
    />
  ));

  const perimeter = verts.map((v, i) => {
    const next = verts[(i + 1) % 5];
    return (
      <line
        key={`perim-${i}`}
        x1={v.x}
        y1={v.y}
        x2={next.x}
        y2={next.y}
        className="problem-hub-edge"
      />
    );
  });

  const rings = verts.map((v, i) => (
    <circle
      key={`ring-${NODES[i].key}`}
      cx={v.x}
      cy={v.y}
      r={radiusNode}
      className="problem-hub-node-ring"
    />
  ));

  const labels = verts.map((v, i) => {
    const { lines } = NODES[i];
    return (
      <text
        key={`lbl-${NODES[i].key}`}
        x={v.x}
        y={v.y}
        textAnchor="middle"
        dominantBaseline="central"
        className="problem-hub-label-inner"
      >
        {lines.length === 1 ? (
          lines[0]
        ) : (
          <>
            <tspan x={v.x} dy="-0.55em">
              {lines[0]}
            </tspan>
            <tspan x={v.x} dy="1.1em">
              {lines[1]}
            </tspan>
          </>
        )}
      </text>
    );
  });

  return (
    <div
      className="relative w-full max-w-2xl mx-auto"
      data-testid="problem-hub"
      role="img"
      aria-label="Your goal at the center, linked to five gaps: Zero Reminders, No Coach, No Tracking, Missed Workouts, and No Structure. Banner: losing progress."
    >
      <svg
        className="relative w-full h-auto aspect-[660/400] block overflow-visible"
        viewBox="70 125 660 400"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {radials}
        {perimeter}

        <circle
          cx={CX}
          cy={CY}
          r={radiusCenter}
          className="problem-hub-center-ring"
        />

        {rings}

        <text
          x={CX}
          y={CY}
          textAnchor="middle"
          dominantBaseline="central"
          className="problem-hub-center-text"
        >
          <tspan x={CX} dy="-0.55em">
            YOUR
          </tspan>
          <tspan x={CX} dy="1.1em">
            {" GOAL"}
          </tspan>
        </text>

        {labels}

        <g transform={`translate(${CX}, ${CY})`}>
          <g className="problem-hub-losing-drop">
            <circle className="problem-hub-bubble problem-hub-bubble--d0" cx="-30" cy="44" r={2.9} />
            <circle className="problem-hub-bubble problem-hub-bubble--d1" cx="34" cy="47" r={2.45} />
            <circle className="problem-hub-bubble problem-hub-bubble--d2" cx="-17" cy="52" r={2.15} />
            <circle className="problem-hub-bubble problem-hub-bubble--d3" cx="20" cy="41" r={2.65} />
            <circle className="problem-hub-bubble problem-hub-bubble--d4" cx="-5" cy="40" r={2.3} />
            <circle className="problem-hub-bubble problem-hub-bubble--d5" cx="9" cy="55" r={2} />
            <circle className="problem-hub-bubble problem-hub-bubble--d6" cx="-41" cy="50" r={2.2} />
            <text
              x={0}
              y={47}
              textAnchor="middle"
              dominantBaseline="central"
              className="problem-hub-banner-svg"
            >
              LOSING PROGRESS
            </text>
          </g>
        </g>
      </svg>
    </div>
  );
}
